import { BaseClient } from '../BaseClient';
import {
  type BlockedJobsRequest,
  type BlockedJobsResponse,
  // New domain-driven analytics types
  type CustomerAccountsOverviewRequest,
  type CustomerAccountsOverviewResponse,
  type FinancialPerformanceMetricsRequest,
  type FinancialPerformanceMetricsResponse,
  type GetMembershipSalesMetricsRequest,
  type JobProfitabilityRequest,
  type JobProfitabilityResponse,
  type JobProgressKPIsRequest,
  type JobProgressKPIsResponse,
  type JobStatusBreakdownRequest,
  type JobStatusBreakdownResponse,
  type ListCallbackEventsRequest,
  type ListCallbackEventsResponse,
  type ListMembershipSalesRequest,
  type ListMembershipSalesResponse,
  type ListPricingSellSheetsRequest,
  type ListPricingSellSheetsResponse,
  type MembershipSalesMetricsResponse,
  type PricingMetricsRequest,
  type PricingMetricsResponse,
  type ServiceQualityMetricsRequest,
  type ServiceQualityMetricsResponse,
  type TechnicianCallbackAnalysisRequest,
  type TechnicianCallbackAnalysisResponse,
  type TechnicianPerformanceDetailRequest,
  type TechnicianPerformanceDetailResponse,
  type TechnicianPerformanceRequest,
  type TechnicianPerformanceResponse,
} from '../compiled-protos/analytics';
import { AnalyticsServiceClient } from '../compiled-protos/analytics.client';

export class AnalyticsService extends BaseClient {
  private client: AnalyticsServiceClient;

  constructor(host: string, userID?: number) {
    super(host, userID);
    this.client = new AnalyticsServiceClient(this.transport);
  }

  // NEW DOMAIN-DRIVEN RPC METHODS

  async getFinancialPerformanceMetrics(request: FinancialPerformanceMetricsRequest): Promise<FinancialPerformanceMetricsResponse> {
    console.log('🚀 AnalyticsService.getFinancialPerformanceMetrics called:', {
      host: this.host,
      transport: this.transport,
      metadata: this.getMetaData(),
      request: request,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await this.client.getFinancialPerformanceMetrics(request, this.getMetaData());
      console.log('📨 AnalyticsService.getFinancialPerformanceMetrics response:', response);
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('💥 AnalyticsService.getFinancialPerformanceMetrics error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
        errorDetails: err.details,
        host: this.host,
        request: request,
        metadata: this.getMetaData(),
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  async getPricingMetrics(request: PricingMetricsRequest): Promise<PricingMetricsResponse> {
    console.log('🚀 AnalyticsService.getPricingMetrics called:', {
      host: this.host,
      transport: this.transport,
      metadata: this.getMetaData(),
      request: request,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await this.client.getPricingMetrics(request, this.getMetaData());
      console.log('📨 AnalyticsService.getPricingMetrics response:', response);
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('💥 AnalyticsService.getPricingMetrics error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
        errorDetails: err.details,
        host: this.host,
        request: request,
        metadata: this.getMetaData(),
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  async getServiceQualityMetrics(request: ServiceQualityMetricsRequest): Promise<ServiceQualityMetricsResponse> {
    console.log('🚀 AnalyticsService.getServiceQualityMetrics called:', {
      host: this.host,
      transport: this.transport,
      metadata: this.getMetaData(),
      request: request,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await this.client.getServiceQualityMetrics(request, this.getMetaData());
      console.log('📨 AnalyticsService.getServiceQualityMetrics response:', response);
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('💥 AnalyticsService.getServiceQualityMetrics error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
        errorDetails: err.details,
        host: this.host,
        request: request,
        metadata: this.getMetaData(),
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  async getTechnicianPerformance(request: TechnicianPerformanceRequest): Promise<TechnicianPerformanceResponse> {
    console.log('🚀 AnalyticsService.getTechnicianPerformance called:', {
      host: this.host,
      transport: this.transport,
      metadata: this.getMetaData(),
      request: request,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await this.client.getTechnicianPerformance(request, this.getMetaData());
      console.log('📨 AnalyticsService.getTechnicianPerformance response:', response);
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('💥 AnalyticsService.getTechnicianPerformance error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
        errorDetails: err.details,
        host: this.host,
        request: request,
        metadata: this.getMetaData(),
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  async getTechnicianCallbackAnalysis(request: TechnicianCallbackAnalysisRequest): Promise<TechnicianCallbackAnalysisResponse> {
    console.log('🚀 AnalyticsService.getTechnicianCallbackAnalysis called:', {
      host: this.host,
      transport: this.transport,
      metadata: this.getMetaData(),
      request: request,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await this.client.getTechnicianCallbackAnalysis(request, this.getMetaData());
      console.log('📨 AnalyticsService.getTechnicianCallbackAnalysis response:', response);
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('💥 AnalyticsService.getTechnicianCallbackAnalysis error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
        errorDetails: err.details,
        host: this.host,
        request: request,
        metadata: this.getMetaData(),
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  async getJobProfitability(request: JobProfitabilityRequest): Promise<JobProfitabilityResponse> {
    try {
      const response = await this.client.getJobProfitability(request, this.getMetaData());
      console.log(response.response)
      return response.response;
    } catch(error) {
      const err = error as any;
      console.error('💥 AnalyticsService.getJobProfitability error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
        errorDetails: err.details,
        host: this.host,
        request: request,
        metadata: this.getMetaData(),
        timestamp: new Date().toISOString(),
      });
      throw error;
    }

  }

  async getTechnicianPerformanceDetail(request: TechnicianPerformanceDetailRequest): Promise<TechnicianPerformanceDetailResponse> {
    console.log('🚀 AnalyticsService.getTechnicianPerformanceDetail called:', {
      host: this.host,
      transport: this.transport,
      metadata: this.getMetaData(),
      request: request,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await this.client.getTechnicianPerformanceDetail(request, this.getMetaData());
      console.log('📨 AnalyticsService.getTechnicianPerformanceDetail response:', response);
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('💥 AnalyticsService.getTechnicianPerformanceDetail error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
        errorDetails: err.details,
        host: this.host,
        request: request,
        metadata: this.getMetaData(),
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  async getBlockedJobs(request: BlockedJobsRequest): Promise<BlockedJobsResponse> {
    try {
      const response = await this.client.getBlockedJobs(request, this.getMetaData());
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('AnalyticsService.getBlockedJobs error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
      });
      throw error;
    }
  }

  async getJobProgressKPIs(request: JobProgressKPIsRequest): Promise<JobProgressKPIsResponse> {
    try {
      const response = await this.client.getJobProgressKPIs(request, this.getMetaData());
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('AnalyticsService.getJobProgressKPIs error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
      });
      throw error;
    }
  }

  async getJobStatusBreakdown(request: JobStatusBreakdownRequest): Promise<JobStatusBreakdownResponse> {
    try {
      const response = await this.client.getJobStatusBreakdown(request, this.getMetaData());
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('AnalyticsService.getJobStatusBreakdown error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
      });
      throw error;
    }
  }

  async getCustomerAccountsOverview(request: CustomerAccountsOverviewRequest): Promise<CustomerAccountsOverviewResponse> {
    try {
      const response = await this.client.getCustomerAccountsOverview(request, this.getMetaData());
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('AnalyticsService.getCustomerAccountsOverview error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
      });
      throw error;
    }
  }

  async listPricingSellSheets(
    request: ListPricingSellSheetsRequest,
  ): Promise<ListPricingSellSheetsResponse> {
    try {
      const response = await this.client.listPricingSellSheets(request, this.getMetaData());
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('AnalyticsService.listPricingSellSheets error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
      });
      throw error;
    }
  }

  async listCallbackEvents(
    request: ListCallbackEventsRequest,
  ): Promise<ListCallbackEventsResponse> {
    try {
      const response = await this.client.listCallbackEvents(request, this.getMetaData());
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('AnalyticsService.listCallbackEvents error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
      });
      throw error;
    }
  }

  async getMembershipSalesMetrics(
    request: GetMembershipSalesMetricsRequest,
  ): Promise<MembershipSalesMetricsResponse> {
    try {
      const response = await this.client.getMembershipSalesMetrics(request, this.getMetaData());
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('AnalyticsService.getMembershipSalesMetrics error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
      });
      throw error;
    }
  }

  async listMembershipSales(
    request: ListMembershipSalesRequest,
  ): Promise<ListMembershipSalesResponse> {
    try {
      const response = await this.client.listMembershipSales(request, this.getMetaData());
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('AnalyticsService.listMembershipSales error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
      });
      throw error;
    }
  }
}
